import { useRef, useState, useEffect } from "react";
import "@/App.css";
import "@/styles/shared.css";
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation, Outlet } from "react-router-dom";
import axios from "axios";
import { motion, useInView, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Heart, MessageCircle, Lock, Shield, Eye, Check, Search, Sparkles } from "lucide-react";
import { Toaster, toast } from "sonner";
import Lenis from '@studio-freight/lenis';
import { Navigation } from "@/components/shared/Navigation";
import { Footer } from "@/components/shared/Footer";
import AppShell from "@/components/shared/AppShell";
import PrivateShell from "@/components/shared/PrivateShell";
import PrivateGate from "@/components/shared/PrivateGate";
import MetaManager from "@/components/shared/MetaManager";
import { initAnalytics } from "@/lib/analytics";
import { captureFirstTouchUtm } from "@/lib/utm";

// Page imports
import TheAppNewPage from "@/pages/TheAppNewPage";
import PrivatePage from "@/pages/PrivatePage";
// BoundariesPage removed from routing — /safety is the single source of truth for standards/behaviour. The file at /pages/BoundariesPage.jsx is retained on disk for reference only.
import SafetyPage from "@/pages/SafetyPage";
import ReportPage from "@/pages/ReportPage";
import ContactPage from "@/pages/ContactPage";
import MissionPage from "@/pages/MissionPage";
import PressPage from "@/pages/PressPage";
import AccessPage from "@/pages/AccessPage";
import PremiumPage from "@/pages/PremiumPage";
import MembershipPage from "@/pages/MembershipPage";
import HomePageV3 from "@/pages/HomePageV3";
import AdminPage from "@/pages/AdminPage";
import InvitePage from "@/pages/InvitePage";
import AppPreviewPage from "@/pages/AppPreviewPage";
import FAQPage from "@/pages/FAQPage";
import BrandKitPage from "@/pages/BrandKitPage";
import CanvaBrandKitPage from "@/pages/CanvaBrandKitPage";
import VerifiedEncountersPage from "@/pages/VerifiedEncountersPage";
import PrivateDatingAppPage from "@/pages/seo/PrivateDatingAppPage";
import DatingWithoutSwipingPage from "@/pages/seo/DatingWithoutSwipingPage";
import IntentionalDatingPage from "@/pages/seo/IntentionalDatingPage";
import ExclusiveDatingPlatformPage from "@/pages/seo/ExclusiveDatingPlatformPage";
import DatingAppForTransWomenPage from "@/pages/seo/DatingAppForTransWomenPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================================================
// LENIS SMOOTH SCROLL
// ============================================================================
const useLenis = () => {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 0.9,
      easing: (t) => 1 - Math.pow(1 - t, 3),
      orientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1.1,
      touchMultiplier: 1.8,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => lenis.destroy();
  }, []);
};

// ============================================================================
// FADE IN SECTION WRAPPER
// ============================================================================
const FadeSection = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ============================================================================
// AGE GATE
// ============================================================================
const AgeGate = ({ onEnter }) => {
  const handleExit = () => {
    // Neutral safe destination for anyone who is not 18+
    window.location.replace('https://www.google.com');
  };

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      className="age-gate"
      data-testid="age-gate"
    >
      <div className="noise-overlay" />

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 1.0 }}
        className="wordmark"
      >
        NYMPHÉ
      </motion.p>

      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="age-gate-headline age-gate-headline--restrained"
        data-testid="age-gate-headline"
      >
        This space is intended for adults only.
      </motion.h1>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.85, duration: 0.8 }}
        className="age-gate-sub"
        data-testid="age-gate-body"
      >
        By entering, you confirm you are 18 years of age or older.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1, duration: 0.7 }}
        className="age-gate-actions"
      >
        <button
          type="button"
          onClick={onEnter}
          className="enter-btn"
          data-testid="age-gate-enter-btn"
        >
          I am 18 or over
        </button>
        <button
          type="button"
          onClick={handleExit}
          className="age-gate-exit"
          data-testid="age-gate-exit-btn"
        >
          Exit
        </button>
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.8 }}
        className="age-gate-legal"
        data-testid="age-gate-legal"
      >
        Please enter only if this is lawful where you are and appropriate for you to view.
      </motion.p>
    </motion.div>
  );
};

// ============================================================================
// SECTION 1: HERO WITH SCROLL TRANSITION
// ============================================================================
const HeroSection = () => {
  const videoRef = useRef(null);
  const [vh, setVh] = useState(800);

  useEffect(() => {
    const updateVh = () => setVh(window.innerHeight);
    updateVh();
    window.addEventListener('resize', updateVh);
    return () => window.removeEventListener('resize', updateVh);
  }, []);

  const { scrollY } = useScroll();
  
  const bgOpacity = useTransform(scrollY, [0, vh * 0.8], [1, 0]);
  const bgScale = useTransform(scrollY, [0, vh], [1, 1.15]);
  const contentOpacity = useTransform(scrollY, [0, vh * 0.5], [1, 0]);
  const contentY = useTransform(scrollY, [0, vh * 0.5], [0, -50]);
  const overlayOpacity = useTransform(scrollY, [0, vh * 0.7], [0.4, 0.95]);

  const scrollToSignup = () => {
    document.getElementById('signup')?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.playbackRate = 0.8;
      video.play().catch(() => {
        const playOnInteraction = () => {
          video.play();
          document.removeEventListener('click', playOnInteraction);
        };
        document.addEventListener('click', playOnInteraction);
      });
    }
  }, []);

  return (
    <section className="section section-hero" data-testid="hero-section">
      <div className="hero-video-container">
        <motion.div 
          className="hero-video-wrapper"
          style={{ 
            opacity: bgOpacity,
            scale: bgScale
          }}
        >
          <video
            ref={videoRef}
            className="hero-video"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            src="/hero-circle-v2.mp4"
          />
        </motion.div>
      </div>

      <motion.div className="hero-overlay" style={{ opacity: overlayOpacity }} />

      <motion.div 
        className="section-inner"
        style={{ opacity: contentOpacity, y: contentY }}
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="hero-wordmark"
        >
          NYMPHÉ
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="hero-headline"
        >
          Find your kind.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="hero-description"
        >
          A private connection space for trans women,<br />
          femmes, and their admirers.
        </motion.p>

        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.7 }}
          onClick={scrollToSignup}
          className="btn-primary"
          data-testid="hero-cta"
        >
          Join Early Access
        </motion.button>
      </motion.div>
    </section>
  );
};

// ============================================================================
// SECTION 2: WHO IT'S FOR
// ============================================================================
const WhoSection = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "start center"]
  });

  const opacity = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const y = useTransform(scrollYProgress, [0, 1], [60, 0]);

  return (
    <section ref={sectionRef} className="section section-who" data-testid="who-section">
      <motion.div className="section-inner" style={{ opacity, y }}>
        <FadeSection>
          <p className="section-label">Who it's for</p>
        </FadeSection>
        
        <FadeSection delay={0.1}>
          <h2 className="section-headline">
            Built for trans women, femmes,<br />
            and the people who admire them.
          </h2>
        </FadeSection>
        
        <FadeSection delay={0.2}>
          <p className="section-body">
            NYMPHÉ is a dating and connection platform designed specifically for our community. 
            Whether you're looking for romance, friendship, or something in between — 
            this is a space where you belong.
          </p>
        </FadeSection>
      </motion.div>
    </section>
  );
};

// ============================================================================
// SECTION 3: SAFE SPACE
// ============================================================================
const SafeSpaceSection = () => {
  const values = [
    {
      icon: Shield,
      title: "Moderated community",
      desc: "Active moderation keeps the space respectful and safe."
    },
    {
      icon: Heart,
      title: "Intentional connections",
      desc: "Every member is here for meaningful interaction."
    },
    {
      icon: Eye,
      title: "Privacy first",
      desc: "Control who sees you and what you share."
    }
  ];

  return (
    <section className="section section-safe" data-testid="safe-section">
      <div className="section-inner">
        <FadeSection>
          <p className="section-label">Why NYMPHÉ</p>
        </FadeSection>
        
        <FadeSection delay={0.1}>
          <h2 className="section-headline">
            A space built on respect.
          </h2>
        </FadeSection>
        
        <FadeSection delay={0.2}>
          <p className="section-body">
            We've created an intentional environment where everyone 
            is treated with dignity. No tolerance for harassment, 
            discrimination, or bad actors.
          </p>
        </FadeSection>

        <div className="values-grid">
          {values.map((item, i) => (
            <FadeSection key={i} delay={0.3 + i * 0.1} className="value-card">
              <div className="value-icon">
                <item.icon size={22} />
              </div>
              <h3 className="value-title">{item.title}</h3>
              <p className="value-desc">{item.desc}</p>
            </FadeSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// SECTION 5: WHY NYMPHÉ EXISTS
// ============================================================================
const WhySection = () => {
  const sectionRef = useRef(null);
  const image1Ref = useRef(null);
  const image2Ref = useRef(null);
  const image3Ref = useRef(null);

  const { scrollYProgress: progress1 } = useScroll({
    target: image1Ref,
    offset: ["start end", "end start"]
  });
  const { scrollYProgress: progress2 } = useScroll({
    target: image2Ref,
    offset: ["start end", "end start"]
  });
  const { scrollYProgress: progress3 } = useScroll({
    target: image3Ref,
    offset: ["start end", "end start"]
  });

  const y1 = useTransform(progress1, [0, 1], ['-6%', '6%']);
  const y2 = useTransform(progress2, [0, 1], ['-7%', '7%']);
  const y3 = useTransform(progress3, [0, 1], ['-10%', '10%']);
  const scale3 = useTransform(progress3, [0, 0.5, 1], [1, 1.02, 1.03]);

  return (
    <section ref={sectionRef} className="section-why" data-testid="why-section">
      <div className="why-label">WHY NYMPHÉ EXISTS</div>
      
      <div className="why-row why-row-text">
        <FadeSection>
          <p className="why-headline">Most platforms aren't built for this.</p>
        </FadeSection>
      </div>

      <div className="why-row why-row-split">
        <div ref={image1Ref} className="why-image-panel">
          <motion.div 
            className="why-image" 
            style={{ 
              backgroundImage: `url(${process.env.PUBLIC_URL}/editorial-4.jpg)`,
              y: y1
            }}
          />
          <div className="why-image-overlay" />
          <div className="why-image-edge" />
        </div>
        <div className="why-text-panel">
          <FadeSection>
            <p className="why-statement">
              You're filtered.<br />
              Misunderstood.<br />
              Treated like a category.
            </p>
          </FadeSection>
        </div>
      </div>

      <div className="why-row why-row-split why-row-reverse">
        <div className="why-text-panel">
          <FadeSection>
            <p className="why-statement">
              Conversations feel unclear.<br />
              Intentions are hidden.<br />
              You're left guessing.
            </p>
          </FadeSection>
        </div>
        <div ref={image2Ref} className="why-image-panel">
          <motion.div 
            className="why-image" 
            style={{ 
              backgroundImage: `url(${process.env.PUBLIC_URL}/editorial-1.png)`,
              y: y2
            }}
          />
          <div className="why-image-overlay" />
          <div className="why-image-edge why-image-edge-left" />
        </div>
      </div>

      <div className="why-row why-row-accent">
        <FadeSection>
          <p className="why-accent">NYMPHÉ is different.</p>
        </FadeSection>
      </div>

      <div className="why-row why-row-closing">
        <div ref={image3Ref} className="why-closing-image-container">
          <motion.div 
            className="why-closing-bg"
            style={{ 
              backgroundImage: `url(${process.env.PUBLIC_URL}/editorial-3.jpg)`,
              y: y3,
              scale: scale3
            }}
          />
        </div>
        <div className="why-closing-gradient" />
        <div className="why-closing-content">
          <FadeSection>
            <p className="why-closing-primary">
              A space that's clear.<br />
              Direct.<br />
              Safe.
            </p>
          </FadeSection>
          <FadeSection delay={0.15}>
            <p className="why-closing-secondary">
              Built for trans women, femmes,<br />
              and the people who are drawn to them.
            </p>
          </FadeSection>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// SECTION 5: FEATURES
// ============================================================================
const FeaturesSection = () => {
  const features = [
    { icon: MessageCircle, title: "Instant messaging", desc: "Start conversations without waiting for a match." },
    { icon: Heart, title: "Interest signals", desc: "Express attraction without sending a message." },
    { icon: Shield, title: "Verified profiles", desc: "Know who you're talking to with verified members." },
    { icon: Lock, title: "Private galleries", desc: "Share photos only with people you trust." },
    { icon: Search, title: "Advanced filters", desc: "Find exactly who you're looking for." },
    { icon: Sparkles, title: "Curated community", desc: "Quality over quantity. Real people, real connections." }
  ];

  return (
    <section className="section section-features" data-testid="features-section">
      <div className="section-inner">
        <FadeSection>
          <p className="section-label">Features</p>
        </FadeSection>
        
        <FadeSection delay={0.1}>
          <h2 className="section-headline">
            Everything you need.<br />
            Nothing you don't.
          </h2>
        </FadeSection>

        <div className="features-grid">
          {features.map((feature, i) => (
            <FadeSection key={i} delay={0.2 + i * 0.08} className="feature-card">
              <div className="feature-icon">
                <feature.icon size={20} />
              </div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-desc">{feature.desc}</p>
            </FadeSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// SECTION 7: FINAL CTA
// ============================================================================
const CTASection = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/waitlist`, { email });
      if (r.data.success) {
        setSuccess(true);
        toast.success("You're on the list.");
        // Facebook Pixel: Track Lead event on successful waitlist signup
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Lead', {
            content_name: 'Waitlist Signup',
            content_category: 'Early Access'
          });
        }
      } else {
        toast.error(r.data.message);
      }
    } catch {
      toast.error("Something went wrong.");
    }
    setLoading(false);
  };

  return (
    <section id="signup" className="section section-cta-final" data-testid="cta-section">
      <div className="cta-final-inner">
        <FadeSection>
          <h2 className="cta-final-headline">Find your kind.</h2>
        </FadeSection>
        
        <FadeSection delay={0.1}>
          <p className="cta-final-sub">Early access is limited.</p>
        </FadeSection>

        <FadeSection delay={0.2}>
          {!success ? (
            <form onSubmit={handleSubmit} className="cta-final-form">
              <input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="cta-final-input"
                data-testid="waitlist-email-input"
                required
              />
              <button type="submit" disabled={loading} className="cta-final-btn" data-testid="waitlist-submit-btn">
                {loading ? "..." : "Join early access"}
              </button>
            </form>
          ) : (
            <div className="cta-final-success">
              <Check size={18} />
              <span>You're on the list</span>
            </div>
          )}
        </FadeSection>
      </div>
    </section>
  );
};

// ============================================================================
// LEGAL PAGES
// ============================================================================
const TermsPage = () => (
  <div className="legal-page">
    <div className="legal-content">
      <Link to="/" className="legal-back">&larr; Back</Link>
      <h1>Terms of Service</h1>
      <a id="last-updated" />
      <p className="legal-date">Last updated: April 2026</p>

      <h2>1. Acceptance</h2>
      <p>By accessing or using NYMPHÉ, you agree to be bound by these Terms. If you do not agree, do not use the platform.</p>

      <h2>2. Eligibility</h2>
      <p>You must be at least 18 years old to use NYMPHÉ. By creating an account, you confirm that you meet this requirement.</p>

      <h2>3. Account and Access</h2>
      <p>Access to NYMPHÉ is by invitation only. Your invite is personal, non-transferable, and single-use. You are responsible for maintaining the confidentiality of your account credentials.</p>

      <h2>4. Acceptable Use</h2>
      <p>NYMPHÉ is a space built on mutual respect. You agree not to:</p>
      <ul>
        <li>Harass, threaten, or discriminate against other users</li>
        <li>Share explicit content without consent</li>
        <li>Impersonate another person</li>
        <li>Use the platform for commercial solicitation</li>
        <li>Attempt to circumvent privacy or security controls</li>
      </ul>

      <h2>5. Content</h2>
      <p>You retain ownership of content you create. By posting content on NYMPHÉ, you grant us a limited licence to display it within the platform. We do not intend to sell your profile content. We may share limited information with service providers where needed to operate, protect, or improve NYMPHÉ.</p>

      <h2>6. Privacy</h2>
      <p>Your data is handled according to our <Link to="/privacy">Privacy Policy</Link>. We collect only what is necessary to operate the platform and protect our community.</p>

      <h2>7. Termination</h2>
      <p>We reserve the right to suspend or terminate accounts that violate these Terms or our <Link to="/safety">Safety Standards</Link>. You may delete your account at any time.</p>

      <h2>8. Conduct, integrity, and account actions</h2>
      <p>NYMPHÉ may restrict, suspend, or remove accounts where conduct creates safety, trust, misuse, or platform-integrity concerns. This includes situations involving conflicts of interest connected to the operation of NYMPHÉ.</p>

      <h2>9. Liability</h2>
      {/* LEGAL REVIEW REQUIRED: governing law, dispute resolution, and liability wording
          should be confirmed by counsel before public launch. The paragraph below is a
          general placeholder and is not a complete liability or jurisdictional clause. */}
      <p>NYMPHÉ is provided &ldquo;as is.&rdquo; We are not responsible for the conduct of users on or off the platform. We do our best to support a safe environment but cannot guarantee the behaviour of every individual on NYMPHÉ.</p>

      <h2>10. Changes</h2>
      <p>We may update these Terms from time to time. Continued use of NYMPHÉ after changes constitutes acceptance of the revised Terms.</p>

      <h2>11. Contact</h2>
      <p>Questions about these Terms? Reach us at <Link to="/contact">Contact</Link>.</p>

      <p className="legal-print-row">
        <button
          type="button"
          className="legal-print"
          onClick={() => window.print()}
          data-testid="terms-print-link"
        >
          Print this page
        </button>
      </p>
    </div>
  </div>
);

const PrivacyPolicyPage = () => (
  <div className="legal-page">
    <div className="legal-content">
      <Link to="/" className="legal-back">&larr; Back</Link>
      <h1>Privacy Policy</h1>
      <a id="last-updated" />
      <p className="legal-date">Last updated: April 2026</p>

      <p><strong>Privacy is not a feature here. It's the starting point.</strong></p>
      <p>NYMPHÉ is built for adults who value discretion. That shapes every decision we make with your data &mdash; what we ask for, what we keep, and what we will never do with it.</p>

      <h2>1. What we collect</h2>
      <p>We ask for the minimum needed to run the platform and keep it safe.</p>
      <ul>
        <li><strong>Account details</strong> &mdash; email, display name, and the profile fields you choose to fill in.</li>
        <li><strong>Usage signals</strong> &mdash; how you move through the app, anonymised wherever possible.</li>
        <li><strong>Location</strong> &mdash; approximate only, and only if you switch it on. You can switch it off at any time.</li>
        <li><strong>Messages</strong> &mdash; we use standard technical measures designed to help protect information during transmission.</li>
      </ul>

      <h2>2. What we never do</h2>
      <ul>
        <li>We do not sell your data. Not ever, not to anyone.</li>
        <li>We do not share your profile or activity with outside platforms.</li>
        <li>We do not run targeted advertising on you.</li>
        <li>We do not retain your data beyond what is necessary to operate the platform and meet our obligations.</li>
      </ul>

      <h2>3. How we use what we hold</h2>
      <p>Your data works for you, inside NYMPHÉ:</p>
      <ul>
        <li>to run the app and make it better</li>
        <li>to surface profiles that align with your preferences and proximity</li>
        <li>to open conversations only when both people choose to</li>
        <li>to protect the community through moderation and reporting</li>
      </ul>
      <p>It is not used outside that purpose.</p>

      <h2>4. Your controls</h2>
      <p>Where available, NYMPHÉ may provide controls that help you manage profile visibility, location sharing, messaging preferences, account access, and deletion requests. Available options may evolve as the platform grows.</p>

      <h2>5. Security</h2>
      <p>We use access controls and technical safeguards intended to limit unnecessary access to sensitive account information. No system is perfect, and we continue to review and improve our safeguards as the platform matures.</p>

      <h2>6. Service providers</h2>
      <p>We work with service providers that help operate NYMPHÉ &mdash; such as hosting, email delivery, analytics, and payment services. Where appropriate, we use contractual terms designed to protect user information.</p>

      <h2>7. Retention &amp; deletion</h2>
      <p>When you request deletion, we take reasonable steps to remove or de-identify account information, subject to technical, legal, safety, fraud-prevention, and record-keeping requirements.</p>

      <h2>8. Changes</h2>
      <p>We may update this policy as the platform evolves. If something material changes, we will tell you directly &mdash; not quietly in a footer.</p>

      <h2>9. Questions</h2>
      <p>Privacy questions go through <Link to="/contact">Contact</Link>. All enquiries are reviewed directly.</p>

      <p className="legal-print-row">
        <button
          type="button"
          className="legal-print"
          onClick={() => window.print()}
          data-testid="privacy-print-link"
        >
          Print this page
        </button>
      </p>
    </div>
  </div>
);

// ============================================================================
// LANDING PAGE (HOME)
// ============================================================================
const LandingPage = () => {
  useLenis();
  
  return (
    <div className="landing-page" data-testid="landing-page">
      <div className="noise-overlay" />
      
      <HeroSection />
      <div className="hero-spacer" />
      
      <WhoSection />
      <SafeSpaceSection />
      <WhySection />
      <FeaturesSection />
      <CTASection />
    </div>
  );
};

// ============================================================================
// SCROLL TO TOP ON ROUTE CHANGE
// ============================================================================
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// ============================================================================
// APP
// ============================================================================
// Routes exempt from the age gate — internal tools + private/invited flows
// that arrive via direct link and already imply the user crossed the gate
// (or, in /admin's case, are not consumer-facing).
const AGE_GATE_EXEMPT_PREFIXES = ['/admin', '/access', '/invite', '/app-preview'];

const AppBody = () => {
  const location = useLocation();
  const [entered, setEntered] = useState(() => localStorage.getItem('nymphe_entered') === 'true');
  const exempt = AGE_GATE_EXEMPT_PREFIXES.some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
  );
  const showGate = !entered && !exempt;
  const showRoutes = entered || exempt;

  return (
    <>
      <ScrollToTop />
      <MetaManager />
      <Toaster position="top-center" toastOptions={{ style: { background: "#0a0a0a", color: "#fff", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "12px" } }} />

      <AnimatePresence mode="wait">
        {showGate && (
          <AgeGate
            key="gate"
            onEnter={() => { localStorage.setItem('nymphe_entered', 'true'); setEntered(true); }}
          />
        )}
      </AnimatePresence>

      {showRoutes && (
        <Routes>
          {/* Public — nymphe.co layer (full nav + footer) */}
          <Route element={<AppShell />}>
            <Route path="/" element={<HomePageV3 />} />
            <Route path="/the-app" element={<TheAppNewPage />} />
            <Route path="/the-app-new" element={<Navigate to="/the-app" replace />} />
            <Route path="/premium" element={<PremiumPage />} />
            <Route path="/membership" element={<MembershipPage />} />
            <Route path="/access" element={<AccessPage />} />
            <Route path="/private" element={<PrivatePage />} />
            <Route path="/boundaries" element={<Navigate to="/safety" replace />} />
            <Route path="/safety" element={<SafetyPage />} />
            <Route path="/report" element={<ReportPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/mission" element={<MissionPage />} />
            <Route path="/press" element={<PressPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/brand-kit" element={<BrandKitPage />} />
            <Route path="/canva-brand-kit" element={<CanvaBrandKitPage />} />
            <Route path="/verified-encounters" element={<VerifiedEncountersPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            {/* SEO discovery landing pages — public, indexable */}
            <Route path="/private-dating-app" element={<PrivateDatingAppPage />} />
            <Route path="/dating-without-swiping" element={<DatingWithoutSwipingPage />} />
            <Route path="/intentional-dating" element={<IntentionalDatingPage />} />
            <Route path="/exclusive-dating-platform" element={<ExclusiveDatingPlatformPage />} />
            <Route path="/dating-app-for-trans-women" element={<DatingAppForTransWomenPage />} />
            <Route path="/admin" element={<AdminPage />} />
          </Route>

          {/* Private — nymphe.app layer (no nav, no footer, token-gated) */}
          <Route element={<PrivateShell />}>
            <Route path="/invite/:token" element={<InvitePage />} />
            <Route path="/app-preview" element={<PrivateGate><AppPreviewPage /></PrivateGate>} />
          </Route>
        </Routes>
      )}
    </>
  );
};

function App() {
  useEffect(() => { captureFirstTouchUtm(); initAnalytics(); }, []);

  return (
    <BrowserRouter>
      <AppBody />
    </BrowserRouter>
  );
}

export default App;
